import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'
import { useAdminRoutes } from '@shared/lib/routes'

import { DisciplineScheduleFlow } from './enums'
import { useDisciplineScheduleStore } from './schedule/store'
import { useDisciplineScheduleAvailabilityApi } from '../api/schedule-availability.hook'
import { useDisciplineScheduleContext } from './discipline-schedule-context.hook'
import {
  SCHEDULE_WEEKS_COUNT,
  buildAvailabilityGrid,
  createScheduleGrid,
  toScheduleDay,
} from './discipline-schedule'

interface ModalDisciplineScheduleSlotsReturn {
  refIsChange: ComputedRef<boolean>
  refTitle: ComputedRef<string>
  refHasBack: ComputedRef<boolean>
  refChooseText: ComputedRef<string>
  refErrorText: ComputedRef<string>
  refHasError: ComputedRef<boolean>
  refTimezoneText: ComputedRef<string>
  refScheduleUrl: ComputedRef<string>
  refIsLoading: ComputedRef<boolean>
  refIsDisabled: ComputedRef<boolean>
  close: () => void
  back: () => void
  confirm: () => void
}

/** The hour picker. Changing the schedule shows the current tutor's hours and must keep the pace; Create and
 *  Change tutor start from every hour, as the tutor is chosen later. */
export const useModalDisciplineScheduleSlots = (): ModalDisciplineScheduleSlotsReturn => {
  const dayjs = useDayjs()
  const store = useDisciplineScheduleStore()
  const {
    refDetails,
    refDisciplineId,
    refCustomerTimezone,
    refDisciplineLabel,
    refCustomerDisciplineLabel,
    refTutorName,
    formatCustomerNow,
    close,
    back,
  } = useDisciplineScheduleContext()

  const refIsChange = computed<boolean>(() => store.getFlow === DisciplineScheduleFlow.Change)
  const refTitle = computed<string>(() => (refIsChange.value ? 'Change weekly schedule' : 'Create weekly schedule'))
  const refHasBack = computed<boolean>(() => store.getFlow === DisciplineScheduleFlow.ChangeTutor)
  const refPace = computed<number>(() => refDetails.value?.pace ?? 0)
  const refPaceLabel = computed<string>(() => (refPace.value ? `${refPace.value} ` : ''))

  const refChooseText = computed<string>(() => (refIsChange.value
    ? `Choose ${refPace.value} weekly lesson days & times from tutor ${refTutorName.value} available slots`
    : `Choose ${refPaceLabel.value}weekly lesson days & times`))

  const refErrorText = computed<string>(() => (refIsChange.value
    ? `You need to choose ${refPace.value} weekly ${refDisciplineLabel.value} lessons date`
    : `You need to choose ${refPaceLabel.value}weekly ${refCustomerDisciplineLabel.value} lessons date`))

  const refHasError = computed<boolean>(() => store.hasSlotsError)

  const refTimezoneText = computed<string>(() => `Customer timezone: ${refCustomerTimezone.value} (${formatCustomerNow()})`)

  /** The tutor's calendar opens on the current week. */
  const refScheduleUrl = computed<string>(() => {
    const tutorId = refDetails.value?.tutor_id
    if (!tutorId) return ''

    return useAdminRoutes('admin.tutor-schedule.week', {
      from: dayjs.utc().startOf('isoWeek').format(DateFormat.Default),
      tutor_id: tutorId,
    }) ?? ''
  })

  const refIsLoading = computed<boolean>(() => store.isGridLoading)
  const refIsDisabled = computed<boolean>(() => store.getSelected.length === 0)

  const loadTutorHours = async (): Promise<void> => {
    store.setGridLoading(true)

    const availability = await useDisciplineScheduleAvailabilityApi({ disciplineId: refDisciplineId.value })

    if (availability) {
      store.setWeeksCount(availability.weeks_ahead || SCHEDULE_WEEKS_COUNT)
      store.setGrid(buildAvailabilityGrid(availability))
    }

    store.setGridLoading(false)
  }

  const confirm = (): void => {
    if (refIsChange.value && store.getSelected.length !== refPace.value) {
      store.setSlotsError(true)
      return
    }

    store.setSlotsError(false)
    store.nextStep()
  }

  onMounted(() => {
    // Back from a later step keeps the hours already picked.
    if (store.getFlow !== DisciplineScheduleFlow.Create && !store.getSelected.length) {
      store.setSelected((refDetails.value?.schedule?.days ?? []).map(toScheduleDay))
    }

    if (refIsChange.value) {
      void loadTutorHours()
      return
    }

    store.setGrid(createScheduleGrid())
  })

  return {
    refIsChange,
    refTitle,
    refHasBack,
    refChooseText,
    refErrorText,
    refHasError,
    refTimezoneText,
    refScheduleUrl,
    refIsLoading,
    refIsDisabled,
    close,
    back,
    confirm,
  }
}
